import { hoursToSeconds } from 'date-fns';

import type { PickupSlotsPolicy } from '~/api/Catalog';
import type { DeliverySlot } from '~/api/ETA';

import type { SlotDeliveryDetails } from './Delivery';

/**
 * The pickup-slot policy the checkout acts on.
 *
 * Once a `/calculate` of this click-and-collect cart has stated the policy (in its
 * response or through a rejection), that verdict wins. Until then the ETA answer stands
 * in for it: a pickup point that publishes pickup windows has the feature on. Waiting for
 * `/calculate` instead deadlocks while the pickup point is closed — the first, slotless
 * `/calculate` cannot be priced, so the policy never arrives and no window can be chosen.
 * `required` is never assumed: only the backend can make a window mandatory.
 */
export const effectivePickupSlotsPolicy = (
  calculated: PickupSlotsPolicy,
  isCalculatedKnown: boolean,
  etaOffersPickupSlots: boolean,
): PickupSlotsPolicy =>
  isCalculatedKnown
    ? calculated
    : { enabled: etaOffersPickupSlots, required: false };

/**
 * The `/calculate` rejection code to act on for a click-and-collect cart.
 *
 * An ASAP pickup refused as `DELIVERY_NOT_AVAILABLE` while the pickup point publishes
 * bookable windows means the point is closed right now (the backend reports this as
 * `PICKUP_SLOT_REQUIRED` since JS-14685, older backends and clock edges around opening
 * hours still send the generic code). The windows prove the point exists, so treat it
 * as "choose a window" instead of the dead-end "Could not calculate the order".
 *
 * Judged by the refused request, not by the checkout as it is now: an answer can arrive
 * after the customer has switched method or pickup point. Only an ASAP pickup request
 * (`sentAsapPickup`) for the pickup point the checkout is still on (`isSamePickupCart`)
 * qualifies; a refusal of a request that already carried a window keeps its own code.
 */
export const pickupRejectionCode = ({
  code,
  sentAsapPickup,
  isSamePickupCart,
  bookableWindowsCount,
}: {
  code: string;
  sentAsapPickup: boolean;
  isSamePickupCart: boolean;
  bookableWindowsCount: number;
}): string =>
  code === 'DELIVERY_NOT_AVAILABLE' &&
  sentAsapPickup &&
  isSamePickupCart &&
  bookableWindowsCount > 0
    ? 'PICKUP_SLOT_REQUIRED'
    : code;

/**
 * Windows the customer can actually be given for self-collection.
 *
 * Mirrors the filter `useDeliverySlotsInfo` (the picker's shared hook, also used
 * by courier delivery) applies to `checkoutStore.deliverySlots`: `is_active`,
 * free capacity, an unexpired `cut_off_time`, and `delivery_min_time` against the
 * cart's backorder floor. Both filters must be kept in sync by hand — there is no
 * shared implementation — or the store can pre-select a window the picker would
 * not have offered, and the reverse.
 */
export const filterBookablePickupSlots = (
  slots: DeliverySlot[] | null,
  now: number,
  backorderFloor: number,
): DeliverySlot[] =>
  (slots ?? []).filter(
    (slot) =>
      slot.cut_off_time > now &&
      slot.is_active &&
      slot.remaining_capacity > 0 &&
      slot.delivery_min_time >= backorderFloor,
  );

/**
 * Earliest moment the cart can be handed over. `useSlots` derives it from the
 * *smallest* backorder lead time in the leg — its `sort().at(0)` takes the
 * minimum despite the local name — and the pickup path must use the identical
 * floor or the picker and the store disagree about which windows exist.
 */
export const backorderFloorFor = (
  leadTimesHours: number[],
  now: number,
): number =>
  now + hoursToSeconds(leadTimesHours.length ? Math.min(...leadTimesHours) : 0);

/**
 * The selection a self-collection leg carries. Built in one place so both
 * rebuild branches emit the same key order.
 */
export const pickupSlotSelection = (
  slot: DeliverySlot,
  isAutomatic: boolean,
): SlotDeliveryDetails => ({
  scheduleSlotId: slot.schedule_slot_id,
  currentDate: slot.current_date,
  ...(isAutomatic ? { isAutomatic: true } : {}),
});

/** The window a self-collection leg is rebuilt with; `null` is ASAP pickup. */
export type PickupWindowChoice = {
  slot: DeliverySlot;
  isAutomatic: boolean;
} | null;

/**
 * Which window a self-collection leg carries after a rebuild, given the windows that can
 * be booked (`available`, earliest first) and the leg's previous selection.
 *
 * - A previous window that is still bookable is kept, in its mode.
 * - With no ASAP alternative — slots required, or the pickup point closed right now —
 *   the earliest window is pre-selected as a real choice so the order can be placed
 *   right away. Replacing a previous window that expired is not a first pick, so the
 *   replacement is `isAutomatic` and the checkout button asks to reconfirm it.
 * - Otherwise ASAP stays the default, unless the customer had switched into slot mode
 *   (an automatic window): then the next window takes its place instead of the
 *   delivery snapping back to ASAP, which would flip the toggle under their hands.
 */
export const choosePickupWindow = ({
  available,
  previous,
  required,
  isPickupOpenNow,
}: {
  available: DeliverySlot[];
  previous: SlotDeliveryDetails | undefined;
  required: boolean;
  isPickupOpenNow: boolean;
}): PickupWindowChoice => {
  const [earliest] = available;

  if (!earliest) {
    return null;
  }

  const kept =
    previous &&
    available.find(
      ({ schedule_slot_id, current_date }) =>
        schedule_slot_id === previous.scheduleSlotId &&
        current_date === previous.currentDate,
    );

  if (kept) {
    return {
      slot: kept,
      isAutomatic: !required && Boolean(previous.isAutomatic),
    };
  }

  if (required || !isPickupOpenNow) {
    return { slot: earliest, isAutomatic: Boolean(previous) };
  }

  if (!previous?.isAutomatic) {
    return null;
  }

  return { slot: earliest, isAutomatic: true };
};

/** Same window and same mode, compared field-wise so key order cannot fake a change. */
export const isSameSlotSelection = (
  a: SlotDeliveryDetails | undefined,
  b: SlotDeliveryDetails | undefined,
): boolean =>
  a?.scheduleSlotId === b?.scheduleSlotId &&
  a?.currentDate === b?.currentDate &&
  Boolean(a?.isAutomatic) === Boolean(b?.isAutomatic);
